/** Ajouter / connecter Stripe, PayPal, GoCardless. Clés sur DD02, pas dans le bundle. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { useCompany } from "../../company/CompanyProvider";
import type { GcMandateRow, GcMandateSync, GcSetup } from "../../payments/gocardless";
import { GATEWAY_CATALOG, mergeGateways, type GatewayProvider, type GatewayRow } from "../../payments/gateways";
import { isCompanyOwner } from "./owner";

function MandateWho({ row, extra }: { row: GcMandateRow; extra?: string }) {
  const account = row.bank_last4 ? `compte •••• ${row.bank_last4}` : "";
  const bits = [account, row.email || "E-mail vide", row.gc_address || "", extra || ""].filter((part) => part !== "");
  return (
    <div>
      <strong>{row.gc_name || row.email || row.mandate_id}</strong>
      <p>{bits.join(" — ")}</p>
    </div>
  );
}

function WaitLabel({ label }: { label: string }) {
  return (
    <span className="fx-gc-sync__wait">
      <span className="fx-gc-spin" aria-hidden />
      {label}
    </span>
  );
}

function seedReadyPicks(sync: GcMandateSync): Record<string, number> {
  const next: Record<string, number> = {};
  for (const row of sync.ready) {
    const id = Number(row.client_id) || 0;
    if (id > 0) next[row.mandate_id] = id;
  }
  return next;
}

function chosenLinks(sync: GcMandateSync, picks: Record<string, number>): GcMandateRow[] {
  const readyIds = new Set(sync.ready.map((row) => row.mandate_id));
  const out: GcMandateRow[] = [];
  for (const row of [...sync.ready, ...sync.doubt]) {
    const picked = picks[row.mandate_id];
    const clientId =
      picked !== undefined ? Number(picked) || 0 : readyIds.has(row.mandate_id) ? Number(row.client_id) || 0 : 0;
    if (clientId > 0) out.push({ ...row, client_id: clientId });
  }
  return out;
}

export function PaymentsGateways() {
  const { company } = useCompany();
  const [rows, setRows] = useState<GatewayRow[]>([]);
  const [open, setOpen] = useState<GatewayProvider | null>(null);
  const [publicKey, setPublicKey] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState<string>("");
  const [err, setErr] = useState("");
  const [hookSecret, setHookSecret] = useState("");
  const [hookOpen, setHookOpen] = useState(false);
  const [setup, setSetup] = useState<GcSetup | null>(null);
  const [sync, setSync] = useState<GcMandateSync | null>(null);
  const [picks, setPicks] = useState<Record<string, number>>({});
  const [notice, setNotice] = useState("");
  const [linkTarget, setLinkTarget] = useState("");

  useEffect(() => {
    if (!company) return;
    setErr("");
    facturxFetch<{ gateways: GatewayRow[] }>(`payment-gateways.php?company_id=${company.id}`)
      .then((data) => setRows(mergeGateways(data.gateways)))
      .catch((e) => setErr(e instanceof Error ? e.message : "Passerelles indisponibles"));
    facturxFetch<{ setup: GcSetup }>(`gocardless-setup.php?company_id=${company.id}`)
      .then((data) => setSetup(data.setup))
      .catch(() => setSetup(null));
  }, [company]);

  if (!company) return null;
  const companyId = company.id;
  const owner = isCompanyOwner(company);
  const gcConnected = rows.some((r) => r.provider === "gocardless" && r.connected);
  const chosen = sync ? chosenLinks(sync, picks) : [];

  async function loadSync() {
    setBusy("gcSYNC");
    setErr("");
    setNotice("");
    try {
      const data = await facturxFetch<{ sync: GcMandateSync }>(`gocardless-mandates.php?company_id=${companyId}`);
      setSync(data.sync);
      setPicks(seedReadyPicks(data.sync));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Impossible de lire les mandats.");
    } finally {
      setBusy("");
    }
  }

  async function applyLinks(rowsToLink: GcMandateRow[]) {
    const links = rowsToLink
      .map((r) => ({ mandate_id: r.mandate_id, client_id: Number(r.client_id) || 0 }))
      .filter((r) => r.mandate_id && r.client_id > 0);
    if (links.length === 0) return;
    setLinkTarget(links.length === 1 ? links[0].mandate_id : "*");
    setBusy("gcLINK");
    setErr("");
    setNotice("");
    try {
      const data = await facturxFetch<{ linked: number; sync: GcMandateSync }>("gocardless-mandates.php", {
        method: "POST",
        body: JSON.stringify({ company_id: companyId, links }),
      });
      setSync(data.sync);
      setPicks(seedReadyPicks(data.sync));
      setNotice(`${data.linked} mandat${data.linked > 1 ? "s" : ""} rattaché${data.linked > 1 ? "s" : ""}.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Lien impossible.");
    } finally {
      setBusy("");
      setLinkTarget("");
    }
  }

  function resetForm() {
    setOpen(null);
    setPublicKey("");
    setSecret("");
  }

  async function call(method: string, provider: GatewayProvider, extra: Record<string, string> = {}) {
    setBusy(provider + method);
    setErr("");
    try {
      const data = await facturxFetch<{ gateways: GatewayRow[] }>("payment-gateways.php", {
        method,
        body: JSON.stringify({ company_id: companyId, provider, ...extra }),
      });
      setRows(mergeGateways(data.gateways));
      if (method === "PATCH") resetForm();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Passerelles</h2>
        <p>
          Ajouter un compte, puis coller les clés. GoCardless : le client signe le mandat sur le portail, ou vous
          rattachez un mandat déjà actif. Vous prélevez une facture envoyée ; « Payé » se pose tout seul.
        </p>
      </header>
      <ul className="fx-gw">
        {GATEWAY_CATALOG.map((item) => {
          const row = rows.find((r) => r.provider === item.id) ?? {
            provider: item.id,
            status: "absent" as const,
            connected: false,
          };
          const added = row.status !== "absent";
          const connecting = open === item.id;
          const locked = busy.startsWith(item.id);
          return (
            <li key={item.id} className="fx-gw__card">
              <div className="fx-gw__top">
                <div>
                  <strong>{item.label}</strong>
                  <p>{item.hint}</p>
                </div>
                <span className={`fx-gw__st is-${row.connected ? "on" : added ? "mid" : "off"}`}>
                  {row.connected ? "Connecté" : added ? "Ajouté" : "Absent"}
                </span>
              </div>
              {connecting ? (
                <form
                  className="fx-gw__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void call("PATCH", item.id, {
                      public_key: publicKey,
                      secret_key: secret,
                      client_id: publicKey,
                      access_token: secret,
                    });
                  }}
                >
                  {item.id !== "gocardless" ? (
                    <Field
                      label={item.id === "stripe" ? "Clé publique (pk_…)" : "Client ID"}
                      value={publicKey}
                      autoComplete="off"
                      onChange={(e) => setPublicKey(e.target.value)}
                    />
                  ) : null}
                  <Field
                    label={item.id === "gocardless" ? "Jeton d’accès" : "Clé secrète"}
                    type="password"
                    value={secret}
                    autoComplete="new-password"
                    onChange={(e) => setSecret(e.target.value)}
                  />
                  <div className="fx-gw__acts">
                    <Button variant="ghost" disabled={locked} onClick={resetForm}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={locked || !owner}>
                      Enregistrer les clés
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="fx-gw__acts">
                  {!added ? (
                    <Button disabled={locked || !owner} onClick={() => void call("POST", item.id)}>
                      Ajouter
                    </Button>
                  ) : null}
                  {added ? (
                    <Button disabled={locked || !owner} onClick={() => setOpen(item.id)}>
                      {row.connected ? "Reconnecter" : "Connecter"}
                    </Button>
                  ) : null}
                  {added ? (
                    <Button variant="ghost" disabled={locked || !owner} onClick={() => void call("DELETE", item.id)}>
                      Retirer
                    </Button>
                  ) : null}
                </div>
              )}
              {item.id === "gocardless" && row.connected && setup ? (
                setup.webhook_secret_set && !hookOpen ? (
                  <div className="fx-gw__hook is-on">
                    <span className="fx-gw__st is-on">Webhook actif</span>
                    <p>« Payé » se pose tout seul à la confirmation.</p>
                    {owner ? (
                      <Button variant="ghost" disabled={locked} onClick={() => setHookOpen(true)}>
                        Modifier
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="fx-gw__form">
                    <p className="fx-co-form__hint m-0">
                      {setup.webhook_secret_set
                        ? "Pour remplacer le secret, collez-en un nouveau. L’URL ne change pas."
                        : "Créez un endpoint neuf vers cette URL, puis collez son secret."}
                    </p>
                    <Field label="URL webhook FacturX" value={setup.webhook_url} readOnly onChange={() => undefined} />
                    <Field
                      label={setup.webhook_secret_set ? "Nouveau secret webhook" : "Secret webhook"}
                      type="password"
                      value={hookSecret}
                      autoComplete="new-password"
                      onChange={(e) => setHookSecret(e.target.value)}
                    />
                    <div className="fx-gw__acts">
                      {setup.webhook_secret_set ? (
                        <Button
                          variant="ghost"
                          disabled={locked}
                          onClick={() => {
                            setHookOpen(false);
                            setHookSecret("");
                          }}
                        >
                          Fermer
                        </Button>
                      ) : null}
                      <Button
                        disabled={locked || !owner || hookSecret.trim().length < 8}
                        onClick={() => {
                          setBusy("gocardlessHOOK");
                          setErr("");
                          facturxFetch<{ setup: GcSetup }>("gocardless-setup.php", {
                            method: "PATCH",
                            body: JSON.stringify({ company_id: companyId, webhook_secret: hookSecret.trim() }),
                          })
                            .then((data) => {
                              setSetup(data.setup);
                              setHookSecret("");
                              setHookOpen(false);
                            })
                            .catch((e) => setErr(e instanceof Error ? e.message : "Erreur"))
                            .finally(() => setBusy(""));
                        }}
                      >
                        Enregistrer le secret
                      </Button>
                    </div>
                  </div>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      {gcConnected && owner ? (
        <div className="fx-gc-sync">
          <h3>Mandats déjà signés</h3>
          <p className="fx-co-form__hint m-0">
            On lit les mandats actifs chez GoCardless et on les accroche au client FacturX, par e-mail. Personne ne
            re-signe.
          </p>
          <div className="fx-gw__acts" style={{ justifyContent: "flex-start" }}>
            <Button disabled={busy !== ""} onClick={() => void loadSync()}>
              {busy === "gcSYNC" ? <WaitLabel label="Lecture…" /> : sync ? "Actualiser" : "Chercher les mandats"}
            </Button>
            {chosen.length > 0 ? (
              <Button disabled={busy !== ""} onClick={() => void applyLinks(chosen)}>
                {linkTarget === "*" ? <WaitLabel label="Liaison…" /> : `Lier les ${chosen.length} choisis`}
              </Button>
            ) : null}
          </div>
          {sync ? (
            <ul className="fx-gc-sync__stats">
              <li>{sync.counts.linked} déjà liés</li>
              <li>{sync.counts.ready} prêts</li>
              <li>{sync.counts.doubt} à confirmer</li>
              <li>{sync.counts.orphan} sans client</li>
            </ul>
          ) : null}
          {sync && sync.ready.length > 0 ? (
            <div className="fx-gc-sync__block">
              <h3>Prêts à lier</h3>
              <p className="fx-co-form__hint m-0">
                L’adresse sous le nom est celle du mandat. Elle indique le salon. Corrige le client si besoin, vide la
                liste pour laisser la ligne de côté, puis Lier les choisis.
              </p>
              {sync.ready.map((row) => (
                <div key={row.mandate_id} className="fx-gc-sync__row">
                  <MandateWho row={row} />
                  <select
                    className="fx-gc-sync__pick"
                    value={picks[row.mandate_id] ?? row.client_id ?? ""}
                    onChange={(e) => setPicks((cur) => ({ ...cur, [row.mandate_id]: Number(e.target.value) || 0 }))}
                  >
                    <option value="">Choisir le client</option>
                    {sync.clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    disabled={busy !== "" || !((picks[row.mandate_id] ?? row.client_id ?? 0) > 0)}
                    onClick={() =>
                      void applyLinks([{ ...row, client_id: picks[row.mandate_id] ?? row.client_id }])
                    }
                  >
                    {linkTarget === row.mandate_id ? <WaitLabel label="Liaison…" /> : "Lier"}
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
          {sync?.doubt.map((row) => (
            <div key={row.mandate_id} className="fx-gc-sync__row">
              <MandateWho row={row} extra="plusieurs clients possibles" />
              <select
                className="fx-gc-sync__pick"
                value={picks[row.mandate_id] ?? ""}
                onChange={(e) => setPicks((cur) => ({ ...cur, [row.mandate_id]: Number(e.target.value) || 0 }))}
              >
                <option value="">Choisir le client</option>
                {sync.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <Button
                disabled={busy !== "" || !(picks[row.mandate_id] > 0)}
                onClick={() => void applyLinks([{ ...row, client_id: picks[row.mandate_id] }])}
              >
                {linkTarget === row.mandate_id ? <WaitLabel label="Liaison…" /> : "Lier"}
              </Button>
            </div>
          ))}
          {sync && sync.orphan.length > 0 ? (
            <p className="fx-co-form__hint">
              Sans fiche client : {sync.orphan.map((r) => r.email || r.gc_name || r.mandate_id).join(", ")}.
            </p>
          ) : null}
        </div>
      ) : null}
      {notice ? <p className="fx-co-form__ok">{notice}</p> : null}
      {err ? <p className="fx-co-form__err">{err}</p> : null}
    </div>
  );
}
